import { parseErrorBody } from './error-body'
import {
  genericErrorMessage,
  messageForCode,
  networkErrorMessage,
  notFoundMessage,
  tooManyRequestsMessage,
  type ErrorMessage,
} from './error-messages'

/**
 * The error every API call rejects with.
 *
 * Baserow returns a JSON body of `{ error, detail }` where `error` is a stable
 * `ERROR_*` code. Wrapping it in a real `Error` matters: TanStack Query, the
 * global error handler and `sonner` all expect an `Error`, and a bare object
 * loses its stack.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly detail: unknown

  constructor(status: number, body: unknown) {
    const parsed = parseErrorBody(body)
    super(parsed.code ?? `Request failed with status ${String(status)}`)
    this.name = 'ApiError'
    this.status = status
    this.code = parsed.code
    this.detail = parsed.detail
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isValidationError(): boolean {
    return this.code === 'ERROR_REQUEST_BODY_VALIDATION'
  }

  /**
   * What to show the user.
   *
   * @param name Names the thing that was being acted on, so a 404 with no code of
   *   its own can say which one -- "Table not found" rather than "Not found".
   */
  userMessage(name?: string): ErrorMessage {
    if (this.status === 429) return tooManyRequestsMessage()

    // A subscription ceiling is the one refusal whose detail is the whole point:
    // the backend names which allotment was reached and what the remedy is, and no
    // static string can. The mapped title still says what kind of refusal it is.
    if (
      this.code === 'ERROR_SUBSCRIPTION_LIMIT_EXCEEDED' &&
      typeof this.detail === 'string'
    ) {
      const mapped = messageForCode(this.code)
      if (mapped !== null) return { ...mapped, description: this.detail }
    }

    const mapped = messageForCode(this.code)
    if (mapped !== null) return mapped

    // A 4xx with a plain-string detail is the backend explaining itself in prose,
    // which beats the generic message. Validation errors are excluded: their
    // detail is a per-field object that means nothing rendered as a sentence.
    if (
      typeof this.detail === 'string' &&
      this.status >= 400 &&
      this.status < 500 &&
      !this.isValidationError
    ) {
      return { title: genericErrorMessage().title, description: this.detail }
    }

    if (this.isNotFound && name !== undefined) return notFoundMessage(name)

    return genericErrorMessage()
  }
}

/**
 * The backend could not be reached at all.
 *
 * Separate from `ApiError` because there is no status and no code to reason
 * about, and because it is the single most common failure on a misconfigured
 * deployment -- where saying "network error" rather than "action not completed"
 * is the difference between a useful report and a useless one.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    // Through the standard `cause` option rather than a field of its own, so the
    // underlying `TypeError` is where anything reading an error chain looks for it.
    super('Could not reach the API server', { cause })
    this.name = 'NetworkError'
  }

  userMessage(): ErrorMessage {
    return networkErrorMessage()
  }
}

/** Both error types this application raises, for the places that handle either. */
export function userMessageFor(error: unknown, name?: string): ErrorMessage {
  if (error instanceof ApiError) return error.userMessage(name)
  if (error instanceof NetworkError) return error.userMessage()
  return genericErrorMessage()
}
